/**
 * UserBadge — shows login state in the nav.
 *
 * - Logged out: renders a "Log ind" button → /login
 * - Logged in: shows display name + logout dropdown
 *
 * Works with both SupabaseAuthProvider (production) and MockAuthProvider (dev).
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, LogIn } from 'lucide-react';
import { container } from '../../services/container';
import { SupabaseAuthProvider } from '../../services/providers/supabase-auth/SupabaseAuthProvider';
import { MockAuthProvider } from '../../services/providers/mock/MockAuthProvider';

function getDisplayName(): string | null {
  const auth = container.auth;
  if (!auth) return null;
  if (auth instanceof SupabaseAuthProvider) {
    const user = auth.getUser();
    if (!user) return null;
    return (user.user_metadata?.full_name as string) ?? user.email ?? null;
  }
  if (auth instanceof MockAuthProvider) {
    const fixture = auth.getCurrentFixture();
    return fixture?.name ?? fixture?.email ?? null;
  }
  return null;
}

export default function UserBadge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [displayName, setDisplayName] = useState<string | null>(getDisplayName);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Subscribe to Supabase auth changes so the badge updates on login/logout
  useEffect(() => {
    const auth = container.auth;
    if (!(auth instanceof SupabaseAuthProvider)) return;
    const client = auth.getClient();
    const { data: { subscription } } = client.auth.onAuthStateChange(() => {
      setDisplayName(getDisplayName());
    });
    return () => subscription.unsubscribe();
  }, []);

  // Logged out → show login button
  if (!displayName) {
    return (
      <button
        onClick={() => navigate('/login', { state: { from: location.pathname } })}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-full text-sm font-medium hover:bg-green-200 dark:hover:bg-green-900/60 transition"
      >
        <LogIn className="w-4 h-4" />
        <span>{t('authSignIn')}</span>
      </button>
    );
  }

  const handleLogout = async () => {
    const auth = container.auth;
    try {
      if (auth instanceof SupabaseAuthProvider) {
        await auth.signOut();
      } else if (auth instanceof MockAuthProvider) {
        await auth.logoutMock();
      }
      setDisplayName(null);
      setIsOpen(false);
      window.location.reload();
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-full text-sm font-medium hover:bg-green-200 dark:hover:bg-green-900/60 transition"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={displayName || undefined}
      >
        <User className="w-4 h-4" />
        <span className="max-w-[120px] truncate">{displayName}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50" role="menu">
          <div className="p-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('loggedInAs', { name: displayName })}</p>
          </div>
          <button
            onClick={handleLogout}
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition"
          >
            {t('logout')}
          </button>
        </div>
      )}
    </div>
  );
}
